import { Callout } from "@bruntworkwear-apps/ui";
import type { ReceiptSource, ReceiptSourceConfig } from "@/shared/api";
import styles from "../UploadForm.module.css";

interface SourceToggleProps {
  config: ReceiptSourceConfig | undefined;
  value: ReceiptSource;
  disabled: boolean;
  onChange: (source: ReceiptSource) => void;
}

/**
 * Chooses between uploading the WC export and reading it from NetSuite.
 *
 * Renders nothing when the deployment has no Celigo token. A control that
 * always fails teaches people the app is broken, and this one fails *after*
 * they have already skipped exporting the workbook.
 *
 * Also renders nothing when `config` is undefined, which is what an API that
 * predates the field returns. The client is deployed separately from the API
 * and can be the newer of the two.
 *
 * Built from two real radio inputs rather than a pair of buttons. The choice is
 * one-of-two and mutually exclusive, which is what a radio group means: arrow
 * keys move between the options, the group is announced as a group, and the
 * selected option is announced as selected. A pair of buttons styled to look
 * selected gets none of that for free.
 *
 * @example
 * <SourceToggle config={config.receipt_source} value="upload"
 *   disabled={false} onChange={setSource} />
 */
export function SourceToggle({ config, value, disabled, onChange }: SourceToggleProps) {
  if (!config?.netsuite_available) return null;

  const options: Array<{ id: ReceiptSource; label: string; hint: string }> = [
    {
      id: "upload",
      label: config.upload_label,
      hint: "Export the saved search from NetSuite and attach it.",
    },
    {
      id: "netsuite",
      label: config.netsuite_label,
      hint: "Read the same saved search over the API. No export needed.",
    },
  ];

  return (
    <div className={styles.source}>
      <fieldset className={styles.sourceGroup} disabled={disabled}>
        <legend className={styles.sourceLegend}>Receipt lines</legend>
        <div className={styles.sourceOptions}>
          {options.map((option) => (
            <label
              key={option.id}
              className={styles.sourceOption}
              data-selected={value === option.id}
            >
              <input
                type="radio"
                name="wc-source"
                value={option.id}
                checked={value === option.id}
                disabled={disabled}
                onChange={() => onChange(option.id)}
              />
              <span className={styles.sourceLabel}>{option.label}</span>
              <span className={styles.sourceHint}>{option.hint}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {value === "netsuite" && (
        <Callout tone="warning">
          The NetSuite feed is new and not yet verified against a full close. If the
          reconciliation looks wrong, re-run it from the uploaded export before acting on it.
        </Callout>
      )}
    </div>
  );
}
